// Onda 2 (2026-06-02): migrado pra Antd — Table + Alert + Tag + Button.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table, Button, Tag, Space, Alert, Empty, Spin, Typography, App as AntApp,
  Checkbox, Popconfirm,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  FilePdfOutlined, SyncOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDate } from './utils';
import { ModalSelecionarRevisor } from './ModalSelecionarRevisor';

// Cor do Tag por status do doc (proposta gerada).
function statusBadgeColor(s: string): string {
  switch (s) {
    case 'rascunho': return 'gold';
    case 'enviada':  return 'blue';
    case 'aprovada': return 'green';
    default:         return 'default';
  }
}

export function AbaPropostas({ negocioId, onGerarNova, onAbrirDoc, reloadFlag }: {
  negocioId: number; onGerarNova: () => void; onAbrirDoc: (id: string) => void; reloadFlag: number;
}) {
  const { message } = AntApp.useApp();
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [docPedindoRevisao, setDocPedindoRevisao] = useState<any | null>(null);

  useEffect(() => { carregar(); }, [negocioId, reloadFlag, mostrarHistorico]);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ proposta_id: String(negocioId), tipo: 'proposta' });
      if (!mostrarHistorico) params.set('vigente', 'true');
      const res = await axios.get(`${API_URL}/comercial/propostas-geradas?${params}`,
        { headers: { Authorization: token } });
      const data = res.data?.body ? JSON.parse(res.data.body) : res.data;
      setDocs(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }
  async function excluir(id: string, titulo: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/comercial/propostas-geradas/${id}`,
        { headers: { Authorization: token } });
      message.success(`Proposta "${titulo}" excluída`);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }
  async function sincronizarTodas() {
    setSincronizando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'sincronizar_negocio', negocio_id: negocioId },
        { headers: { Authorization: token }, timeout: 60000 });
      const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
      message.success(
        `${data.sucesso} proposta(s) sincronizada(s).${data.falhas ? ` ${data.falhas} falha(s).` : ''}`
      );
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSincronizando(false); }
  }

  async function confirmarRevisao(revisor_email: string, comentario: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        {
          action: 'com_solicitar_revisao',
          proposta_gerada_id: docPedindoRevisao!.id,
          revisor_email,
          comentario_geral: comentario || null,
        },
        { headers: { Authorization: token } });
      message.success(`Revisão solicitada a ${revisor_email}`);
      setDocPedindoRevisao(null);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  const desatualizadas = docs.filter(d => d.composicao_desatualizada).length;

  const columns: ColumnsType<any> = [
    { title: 'Número', dataIndex: 'numero_proposta', key: 'numero', width: 110,
      render: (v) => v ? <span style={{ fontFamily: 'monospace', color: '#020D7F' }}>{v}</span> : '—' },
    { title: 'Título', dataIndex: 'titulo', key: 'titulo',
      ellipsis: { showTitle: false },
      render: (v, d) => (
        <Space size={6}>
          <a onClick={() => onAbrirDoc(d.id)} title={v}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}>
            {v}
          </a>
          {d.composicao_desatualizada && (
            <Tag color="gold" bordered={false}
              title="Composição alterada após geração — sincronize">
              !
            </Tag>
          )}
        </Space>
      ) },
    { title: 'Versão', key: 'versao', width: 90, align: 'center',
      render: (_, d) => <span style={{ fontSize: 11 }}>v{d.versao}{d.revisao > 0 ? ` · R${d.revisao}` : ''}</span> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 90, align: 'center',
      render: (v) => <Tag color={statusBadgeColor(v)} bordered={false}>{v}</Tag> },
    { title: 'Revisão', dataIndex: 'status_revisao', key: 'revisao', width: 130, align: 'center',
      render: (v) => {
        if (v === 'em_revisao')         return <Tag color="purple" bordered={false}>📝 em revisão</Tag>;
        if (v === 'aprovada')           return <Tag color="green" bordered={false}>✅ aprovada</Tag>;
        if (v === 'ajustes_solicitados') return <Tag color="orange" bordered={false}>🔄 ajustes</Tag>;
        return <Typography.Text type="secondary">—</Typography.Text>;
      } },
    { title: 'Atualizado', dataIndex: 'atualizado_em', key: 'atualizado', width: 110, align: 'center',
      render: (v) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Typography.Text> },
    { title: 'Ações', key: 'acoes', width: 110, align: 'center',
      render: (_, d) => (
        <Space size={4}>
          {!d.status_revisao && d.tipo === 'proposta' && (
            <Button size="small" icon={<EditOutlined />}
              title="Pedir peer review pra outro vendedor"
              onClick={() => setDocPedindoRevisao(d)}
              style={{ color: '#7C3AED', borderColor: '#7C3AED' }} />
          )}
          <Popconfirm title={`Excluir "${d.titulo}"?`} okText="Excluir" okType="danger" cancelText="Cancelar"
            onConfirm={() => excluir(d.id, d.titulo)}>
            <Button size="small" danger icon={<DeleteOutlined />} title="Excluir definitivamente" />
          </Popconfirm>
        </Space>
      ) },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space size={14} wrap>
          <Typography.Text strong style={{ color: '#020D7F' }}>
            {docs.length} proposta{docs.length === 1 ? '' : 's'} gerada{docs.length === 1 ? '' : 's'}
          </Typography.Text>
          <Checkbox checked={mostrarHistorico}
            onChange={e => setMostrarHistorico(e.target.checked)}>
            <span style={{ fontSize: 12 }}>Mostrar histórico de revisões</span>
          </Checkbox>
        </Space>
        <Button type="primary" icon={<FilePdfOutlined />} onClick={onGerarNova}>
          Gerar nova proposta
        </Button>
      </Space>

      {desatualizadas > 0 && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message={
            desatualizadas === 1
              ? 'Uma proposta está desatualizada'
              : `${desatualizadas} propostas estão desatualizadas`
          }
          description="A composição vinculada foi alterada depois da geração. Os itens e valores podem não refletir o atual."
          action={
            <Popconfirm
              title="Re-sincronizar todas as propostas desatualizadas?"
              description="Re-renderiza o conteúdo das propostas usando a composição atual."
              okText="Sincronizar" cancelText="Cancelar"
              onConfirm={sincronizarTodas}>
              <Button size="small" icon={<SyncOutlined />} loading={sincronizando}>
                Sincronizar todas
              </Button>
            </Popconfirm>
          }
        />
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : docs.length === 0 ? (
        <Empty description="Nenhuma proposta gerada para este negócio" />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={docs} pagination={false}
          size="small" tableLayout="fixed"
          rowClassName={(d) => d.composicao_desatualizada ? 'row-warn' : ''} />
      )}

      {docPedindoRevisao && (
        <ModalSelecionarRevisor
          doc={docPedindoRevisao}
          onClose={() => setDocPedindoRevisao(null)}
          onConfirmar={confirmarRevisao}
        />
      )}
    </div>
  );
}
